import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Users, Star, ThumbsUp, TrendingUp, Loader2, Filter } from 'lucide-react';
import api from '../services/api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardAction,
  CardFooter,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { toast } from '../components/ui/use-toast';

// ─── Types ───────────────────────────────────────────────────────────────────
interface CSATStatistics {
  total_responses: number;
  average_rating: number;
  rating_distribution: { [key: string]: number };
  satisfaction_rate: number;
  response_rate: number;
}

interface CSATResponseDetail {
  id: number;
  conversation_id: number;
  contact_id: number | null;
  contact_name: string | null;
  contact_email: string | null;
  channel_id: number;
  rating: number;
  feedback: string | null;
  submitted_at: string;
}

interface CSATAnalytics {
  statistics: CSATStatistics;
  responses: CSATResponseDetail[];
  total_count: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const RATING_COLORS = ['#f44336', '#ff9800', '#ffc107', '#8bc34a', '#4caf50'];

const RATING_LABELS: Record<number, string> = {
  1: 'Very Dissatisfied',
  2: 'Dissatisfied',
  3: 'Neutral',
  4: 'Satisfied',
  5: 'Very Satisfied',
};

const RATING_BADGE_CLASSES: Record<number, string> = {
  1: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300',
  2: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300',
  3: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300',
  4: 'border-lime-200 bg-lime-50 text-lime-700 dark:border-lime-800 dark:bg-lime-950 dark:text-lime-300',
  5: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
};

// ─── Star Rating Component ────────────────────────────────────────────────────
const StarRating: React.FC<{ value: number; max?: number }> = ({ value, max = 5 }) => (
  <span className="text-amber-400 text-sm tracking-tight" aria-label={`${value} out of ${max} stars`}>
    {Array.from({ length: max }, (_, i) => (i < Math.round(value) ? '★' : '☆')).join('')}
  </span>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toDateInput = (d: Date) => d.toISOString().split('T')[0];

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

// ─── Page ─────────────────────────────────────────────────────────────────────
const CSATAnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<CSATAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState(
    toDateInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  );
  const [endDate, setEndDate] = useState(toDateInput(new Date()));
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [selectedRating, setSelectedRating] = useState<string>('');

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const accountId = 1; // TODO: Get from auth context
      const params: Record<string, string | number> = {};
      if (selectedChannel) params.channel_id = Number(selectedChannel);
      if (selectedRating) params.rating = Number(selectedRating);
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate).toISOString();

      const response = await api.get<CSATAnalytics>(
        `/accounts/${accountId}/csat/analytics`,
        { params }
      );
      setAnalytics(response.data);
    } catch (err: any) {
      toast({
        title: 'Failed to load CSAT analytics',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Chart data ──────────────────────────────────────────────────────────────
  const barSeries = [
    {
      name: 'Responses',
      data: analytics
        ? [1, 2, 3, 4, 5].map(
            (r) => analytics.statistics.rating_distribution[r.toString()] || 0
          )
        : [],
    },
  ];

  const barOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
    plotOptions: {
      bar: { distributed: true, borderRadius: 4, columnWidth: '55%' },
    },
    colors: RATING_COLORS,
    xaxis: {
      categories: ['1 ★', '2 ★', '3 ★', '4 ★', '5 ★'],
      labels: { style: { fontSize: '12px' } },
    },
    yaxis: { labels: { style: { fontSize: '12px' } }, min: 0 },
    legend: { show: false },
    dataLabels: { enabled: false },
    grid: { borderColor: 'hsl(var(--border))', strokeDashArray: 4 },
    tooltip: { theme: 'light' },
  };

  const donutLabels = ['Satisfied (4-5)', 'Neutral (3)', 'Dissatisfied (1-2)'];
  const donutSeries = analytics
    ? [
        (analytics.statistics.rating_distribution['4'] || 0) +
          (analytics.statistics.rating_distribution['5'] || 0),
        analytics.statistics.rating_distribution['3'] || 0,
        (analytics.statistics.rating_distribution['1'] || 0) +
          (analytics.statistics.rating_distribution['2'] || 0),
      ]
    : [0, 0, 0];

  const donutOptions: ApexOptions = {
    chart: { type: 'donut', fontFamily: 'inherit' },
    colors: ['#4caf50', '#ffc107', '#f44336'],
    labels: donutLabels,
    legend: { position: 'bottom', fontSize: '12px' },
    dataLabels: { formatter: (val: number) => `${val.toFixed(1)}%` },
    plotOptions: { pie: { donut: { size: '65%' } } },
    tooltip: { theme: 'light' },
    stroke: { width: 0 },
  };

  // ── KPI values ──────────────────────────────────────────────────────────────
  const stats = analytics?.statistics;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">CSAT Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Customer Satisfaction Survey results and analytics
          </p>
        </div>
        <Button onClick={fetchAnalytics} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Start date */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="start-date">Start Date</Label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            {/* End date */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="end-date">End Date</Label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            {/* Rating filter */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rating-filter">Rating</Label>
              <select
                id="rating-filter"
                value={selectedRating}
                onChange={(e) => setSelectedRating(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">All Ratings</option>
                {[1, 2, 3, 4, 5].map((r) => (
                  <option key={r} value={r}>
                    {r} ★ — {RATING_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            {/* Apply */}
            <div className="flex items-end">
              <Button className="w-full" onClick={fetchAnalytics} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Analytics content */}
      {!loading && analytics && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {/* Total Responses */}
            <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm">
              <CardHeader>
                <CardDescription>Total Responses</CardDescription>
                <CardTitle className="text-3xl font-semibold tabular-nums @[200px]/card:text-4xl">
                  {stats!.total_responses.toLocaleString()}
                </CardTitle>
                <CardAction>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                </CardAction>
              </CardHeader>
              <CardFooter className="text-xs text-muted-foreground">
                Survey responses collected
              </CardFooter>
            </Card>

            {/* Average Rating */}
            <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm">
              <CardHeader>
                <CardDescription>Average Rating</CardDescription>
                <CardTitle className="text-3xl font-semibold tabular-nums @[200px]/card:text-4xl">
                  {stats!.average_rating.toFixed(2)}
                </CardTitle>
                <CardAction>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
                    <Star className="h-5 w-5 text-amber-500" />
                  </div>
                </CardAction>
              </CardHeader>
              <CardFooter>
                <StarRating value={stats!.average_rating} />
              </CardFooter>
            </Card>

            {/* Satisfaction Rate */}
            <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm">
              <CardHeader>
                <CardDescription>Satisfaction Rate</CardDescription>
                <CardTitle className="text-3xl font-semibold tabular-nums @[200px]/card:text-4xl text-emerald-600">
                  {stats!.satisfaction_rate.toFixed(1)}%
                </CardTitle>
                <CardAction>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                    <ThumbsUp className="h-5 w-5 text-emerald-500" />
                  </div>
                </CardAction>
              </CardHeader>
              <CardFooter className="text-xs text-muted-foreground">
                4-5 star ratings
              </CardFooter>
            </Card>

            {/* Response Rate */}
            <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-sm">
              <CardHeader>
                <CardDescription>Response Rate</CardDescription>
                <CardTitle className="text-3xl font-semibold tabular-nums @[200px]/card:text-4xl">
                  {stats!.response_rate.toFixed(1)}%
                </CardTitle>
                <CardAction>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                </CardAction>
              </CardHeader>
              <CardFooter className="text-xs text-muted-foreground">
                Of total conversations
              </CardFooter>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Rating Distribution */}
            <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
              <CardHeader>
                <CardTitle>Rating Distribution</CardTitle>
                <CardDescription>Breakdown of responses by star rating</CardDescription>
              </CardHeader>
              <CardContent>
                <ReactApexChart
                  type="bar"
                  series={barSeries}
                  options={barOptions}
                  height={280}
                />
              </CardContent>
            </Card>

            {/* Satisfaction Overview */}
            <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
              <CardHeader>
                <CardTitle>Satisfaction Overview</CardTitle>
                <CardDescription>Satisfied · Neutral · Dissatisfied</CardDescription>
              </CardHeader>
              <CardContent>
                <ReactApexChart
                  type="donut"
                  series={donutSeries}
                  options={donutOptions}
                  height={280}
                />
              </CardContent>
            </Card>
          </div>

          {/* Responses Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Responses</CardTitle>
              <CardDescription>{analytics.total_count} total responses</CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Feedback</TableHead>
                    <TableHead className="text-right">Conversation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.responses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                        No CSAT responses found for the selected period
                      </TableCell>
                    </TableRow>
                  ) : (
                    analytics.responses.map((response) => (
                      <TableRow key={response.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {formatDate(response.submitted_at)}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">
                            {response.contact_name || 'Anonymous'}
                          </div>
                          {response.contact_email && (
                            <div className="text-xs text-muted-foreground">
                              {response.contact_email}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant="outline"
                              className={RATING_BADGE_CLASSES[response.rating] ?? ''}
                            >
                              {response.rating} ★
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {RATING_LABELS[response.rating]}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[280px]">
                          <p className="text-sm truncate text-muted-foreground">
                            {response.feedback || '—'}
                          </p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              window.location.href = `/conversations/${response.conversation_id}`;
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Empty state */}
      {!loading && !analytics && (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-center gap-2">
          <TrendingUp className="h-12 w-12 text-muted-foreground/40" />
          <p className="text-muted-foreground">No analytics data available.</p>
          <Button variant="outline" onClick={fetchAnalytics}>
            Try Again
          </Button>
        </div>
      )}
    </div>
  );
};

export default CSATAnalyticsPage;
